import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, Loader2, Mail, Send, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { dbService } from '../lib/supabase';
import { buildSponsorInviteCtaHref, buildSponsorInvitePreviewHtml } from '../lib/sponsorInviteEmail';
import {
  applyPreviewGreeting,
  SPONSOR_INVITE_PREVIEW_GREETING,
} from '../lib/sponsorInviteGreeting';
import {
  SPONSOR_INVITE_USE_MINUTE_TEST_DELAYS,
} from '../constants/sponsorInviteCampaign';
import {
  isCampaignEmailValid,
  parseCampaignRecipients,
} from '../lib/campaignEmailInput';

type StepKey = 1 | 2 | 3;

interface TemplateForm {
  subject: string;
  body: string;
}

const EMPTY_TEMPLATE: TemplateForm = { subject: '', body: '' };

interface RecipientRow {
  name: string;
  email: string;
}

const EMPTY_RECIPIENT_ROW: RecipientRow = { name: '', email: '' };

export interface SponsorInviteCampaignPanelProps {
  /** Render inside unified Campaign page (no outer card; parent supplies shell). */
  embedded?: boolean;
}

const sectionClass = 'border border-charcoal/15 rounded-xl p-4 sm:p-5';

const SponsorInviteCampaignPanel: React.FC<SponsorInviteCampaignPanelProps> = ({ embedded = false }) => {
  const [activeStep, setActiveStep] = useState<StepKey>(1);
  const [templates, setTemplates] = useState<Record<StepKey, TemplateForm>>({
    1: { ...EMPTY_TEMPLATE },
    2: { ...EMPTY_TEMPLATE },
    3: { ...EMPTY_TEMPLATE },
  });
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [savingTemplates, setSavingTemplates] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testEmailError, setTestEmailError] = useState<string | null>(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [recipientRows, setRecipientRows] = useState<RecipientRow[]>([{ ...EMPTY_RECIPIENT_ROW }]);
  const [campaignError, setCampaignError] = useState<string | null>(null);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkReplace, setBulkReplace] = useState(false);
  const [bulkFeedback, setBulkFeedback] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [latestCampaignId, setLatestCampaignId] = useState<string | null>(null);
  const [recipients, setRecipients] = useState<
    Awaited<ReturnType<typeof dbService.getSponsorInviteRecipientsForCampaign>>
  >([]);

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const rows = await dbService.getSponsorInviteEmailTemplates();
      const next: Record<StepKey, TemplateForm> = {
        1: { ...EMPTY_TEMPLATE },
        2: { ...EMPTY_TEMPLATE },
        3: { ...EMPTY_TEMPLATE },
      };
      for (const row of rows) {
        const step = row.step as StepKey;
        if (step >= 1 && step <= 3) {
          next[step] = {
            subject: row.subject,
            body: row.body,
          };
        }
      }
      setTemplates(next);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load email templates');
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  const loadLatestRecipients = useCallback(async () => {
    try {
      const campaigns = await dbService.getSponsorInviteCampaigns(1);
      const latest = campaigns[0];
      if (!latest?.id) {
        setLatestCampaignId(null);
        setRecipients([]);
        return;
      }
      setLatestCampaignId(latest.id);
      const rows = await dbService.getSponsorInviteRecipientsForCampaign(latest.id);
      setRecipients(rows);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    void loadTemplates();
    void loadLatestRecipients();
  }, [loadTemplates, loadLatestRecipients]);

  const current = templates[activeStep];

  const updateCurrent = (patch: Partial<TemplateForm>) => {
    setTemplates((prev) => ({
      ...prev,
      [activeStep]: { ...prev[activeStep], ...patch },
    }));
  };

  const saveTemplates = async () => {
    setSavingTemplates(true);
    try {
      for (const step of [1, 2, 3] as StepKey[]) {
        const t = templates[step];
        await dbService.upsertSponsorInviteEmailTemplate({
          step,
          subject: t.subject.trim(),
          body: t.body,
        });
      }
      toast.success('Email templates saved');
    } catch {
      toast.error('Failed to save templates');
    } finally {
      setSavingTemplates(false);
    }
  };

  const previewHtml = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const ctaHref = buildSponsorInviteCtaHref(origin, 'preview-token');
    const unsubscribeUrl = origin
      ? `${origin.replace(/\/$/, '')}/unsubscribe?token=preview`
      : undefined;
    // Greeting is per-recipient at send time; preview shows a representative sample.
    const previewBody = applyPreviewGreeting(current.body, SPONSOR_INVITE_PREVIEW_GREETING);
    return buildSponsorInvitePreviewHtml({
      body: previewBody,
      ctaHref,
      unsubscribeUrl,
    });
  }, [current]);

  const handleTestSend = async () => {
    const trimmed = testEmail.trim().toLowerCase();
    if (!trimmed) {
      setTestEmailError('Enter a test email address');
      toast.error('Enter a test email address');
      return;
    }
    if (!isCampaignEmailValid(trimmed)) {
      setTestEmailError('Enter a valid email address (e.g. name@company.com)');
      toast.error('Enter a valid email address');
      return;
    }
    setTestEmailError(null);
    setSendingTest(true);
    try {
      await dbService.sendSponsorInviteTestEmail({
        step: activeStep,
        testEmail: trimmed,
        origin: typeof window !== 'undefined' ? window.location.origin : undefined,
        subject: current.subject,
        body: current.body,
      });
      toast.success(`Email ${activeStep} test sent to ${trimmed}`);
    } catch {
      toast.error('Test send failed');
    } finally {
      setSendingTest(false);
    }
  };

  const addRecipientRow = () => setRecipientRows((prev) => [...prev, { ...EMPTY_RECIPIENT_ROW }]);

  const updateRecipientRow = (index: number, patch: Partial<RecipientRow>) => {
    setRecipientRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    if (campaignError) setCampaignError(null);
  };

  const removeRecipientRow = (index: number) => {
    setRecipientRows((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0 ? next : [{ ...EMPTY_RECIPIENT_ROW }];
    });
  };

  const applyBulkAdd = () => {
    const { recipients, invalid } = parseCampaignRecipients(bulkText);
    if (recipients.length === 0) {
      setBulkFeedback(null);
      toast.error('No valid recipients found. Use "Name, email" or "email" per line.');
      return;
    }
    const parsedRows: RecipientRow[] = recipients.map((r) => ({ name: r.name ?? '', email: r.email }));
    setRecipientRows((prev) => {
      if (bulkReplace) return parsedRows;
      // Merge into existing non-empty rows, de-duplicating by email.
      const existing = prev.filter((r) => r.email.trim() || r.name.trim());
      const seen = new Set(existing.map((r) => r.email.trim().toLowerCase()).filter(Boolean));
      const merged = [...existing];
      for (const row of parsedRows) {
        const key = row.email.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(row);
      }
      return merged.length > 0 ? merged : [{ ...EMPTY_RECIPIENT_ROW }];
    });
    const invalidNote = invalid.length > 0 ? ` (${invalid.length} line(s) ignored)` : '';
    setBulkFeedback(
      `Added ${recipients.length} recipient${recipients.length === 1 ? '' : 's'}.${invalidNote}`,
    );
    setBulkText('');
    if (campaignError) setCampaignError(null);
  };

  const handleStartCampaign = async () => {
    const cleaned = recipientRows
      .map((r) => ({ name: r.name.trim(), email: r.email.trim().toLowerCase() }))
      .filter((r) => r.email.length > 0 || r.name.length > 0);

    const withEmail = cleaned.filter((r) => r.email.length > 0);
    if (withEmail.length === 0) {
      const msg = 'Add at least one recipient email';
      setCampaignError(msg);
      toast.error(msg);
      return;
    }
    const invalid = withEmail.filter((r) => !isCampaignEmailValid(r.email)).map((r) => r.email);
    if (invalid.length > 0) {
      const msg = `Invalid email(s): ${invalid.slice(0, 3).join(', ')}${invalid.length > 3 ? '…' : ''}`;
      setCampaignError(msg);
      toast.error('Fix invalid email addresses before starting');
      return;
    }
    // De-duplicate by email (first occurrence keeps its name).
    const seen = new Set<string>();
    const recipients: { firstName?: string; email: string }[] = [];
    for (const r of withEmail) {
      if (seen.has(r.email)) continue;
      seen.add(r.email);
      recipients.push(r.name ? { firstName: r.name, email: r.email } : { email: r.email });
    }
    setCampaignError(null);

    setStarting(true);
    try {
      await saveTemplates();
      const result = await dbService.startSponsorInviteCampaign({
        recipients,
        origin: typeof window !== 'undefined' ? window.location.origin : undefined,
      });
      const ok = result.results.filter((r) => r.ok).length;
      const failed = result.results.length - ok;
      if (ok === 0) {
        toast.error('No emails were started. Check duplicates or unsubscribes.');
      } else if (failed > 0) {
        toast.success(`Started ${ok} recipient(s); ${failed} skipped or failed`);
      } else {
        toast.success(`Campaign started for ${ok} recipient(s)`);
      }
      setRecipientRows([{ ...EMPTY_RECIPIENT_ROW }]);
      setBulkText('');
      setBulkFeedback(null);
      await loadLatestRecipients();
    } catch {
      toast.error('Failed to start campaign');
    } finally {
      setStarting(false);
    }
  };

  if (loadingTemplates) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-electric-blue" />
      </div>
    );
  }

  const wrapperClass = embedded
    ? 'flex flex-col flex-1 min-h-0 gap-6'
    : 'rounded-2xl border border-electric-blue/20 bg-white shadow-sm p-6 sm:p-8 flex flex-col gap-8';

  return (
    <div className={wrapperClass}>
      <header className={embedded ? 'mb-0 pb-5 border-b border-charcoal/10 shrink-0' : undefined}>
        <h2
          className={`font-bold text-charcoal flex items-center gap-2 ${
            embedded ? 'text-lg' : 'text-2xl gap-3'
          }`}
        >
          <Mail className={`text-electric-blue shrink-0 ${embedded ? 'w-5 h-5' : 'w-8 h-8'}`} aria-hidden />
          Sponsor invites
        </h2>
        <p className="mt-2 text-sm text-slate-gray max-w-3xl leading-relaxed">
          Invite biotech, pharma, and CMC professionals with a 3-email sequence (immediate, day 3, day 7). Recipients
          choose their own signup email via the invite link. Delivery analytics are available in Resend or Instantly
          (depending on the campaign provider); this page
          tracks sends, link clicks, and sign-ups in-app.
        </p>
        {SPONSOR_INVITE_USE_MINUTE_TEST_DELAYS ? (
          <div
            className="mt-3 max-w-3xl rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-950 leading-relaxed"
            role="status"
          >
            <strong className="font-semibold">Minute test delays enabled</strong> — follow-up emails 2 and 3 use{' '}
            <strong>3 minutes</strong> and <strong>7 minutes</strong> after start (same mapping as CDMO claim test
            mode: 3-day → 3-minute, 7-day → 7-minute). Set <code className="text-[11px]">VITE_SPONSOR_INVITE_USE_MINUTE_TEST_DELAYS=true</code>{' '}
            here and secret{' '}
            <code className="text-[11px]">SPONSOR_INVITE_USE_MINUTE_TEST_DELAYS=true</code> on Edge functions{' '}
            <code className="text-[11px]">process-sponsor-invite-campaign</code> and{' '}
            <code className="text-[11px]">send-sponsor-invite-test-email</code>. Run the sponsor cron at least every
            minute while testing.
          </div>
        ) : null}
      </header>

      <section className={sectionClass}>
        <h2 className="text-lg font-semibold text-charcoal mb-3">Email copy (steps 1–3)</h2>
        {/* <p className="text-xs text-slate-gray mb-4">
          CTA link destination is fixed to your platform invite flow; you can edit subject, body, and button label
          only.
        </p> */}
        <div className="flex gap-2 mb-4">
          {([1, 2, 3] as StepKey[]).map((step) => (
            <button
              key={step}
              type="button"
              onClick={() => setActiveStep(step)}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                activeStep === step
                  ? 'bg-electric-blue text-white'
                  : 'bg-charcoal/5 text-charcoal hover:bg-charcoal/10'
              }`}
            >
              Email {step}
            </button>
          ))}
        </div>

        <div className="grid gap-4 max-w-3xl">
          <div>
            <label htmlFor="template-subject" className="block text-sm font-medium text-charcoal mb-1">Subject</label>
            <input
              id="template-subject"
              type="text"
              value={current.subject}
              onChange={(e) => updateCurrent({ subject: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-charcoal/15 text-sm"
            />
          </div>
          <div>
            <label htmlFor="template-body" className="block text-sm font-medium text-charcoal mb-1">Body</label>
            <textarea
              id="template-body"
              value={current.body}
              onChange={(e) => updateCurrent({ body: e.target.value })}
              rows={12}
              className="w-full px-3 py-2 rounded-lg border border-charcoal/15 text-sm font-mono"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-5">
          <button
            type="button"
            onClick={() => void saveTemplates()}
            disabled={savingTemplates}
            className="px-4 py-2 rounded-lg bg-charcoal/10 text-charcoal text-sm font-medium hover:bg-charcoal/15 disabled:opacity-50"
          >
            {savingTemplates ? 'Saving…' : 'Save templates'}
          </button>
          <button
            type="button"
            onClick={() => setShowPreview((v) => !v)}
            className="px-4 py-2 rounded-lg border border-charcoal/15 text-sm font-medium flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            {showPreview ? 'Hide preview' : 'Preview'}
          </button>
        </div>

        {showPreview ? (
          <div className="mt-4 border border-charcoal/10 rounded-lg overflow-hidden">
            <p className="text-xs font-medium text-slate-gray px-3 py-2 bg-charcoal/5">
              Subject: {current.subject || '(empty)'}
            </p>
            <iframe
              title="Email preview"
              srcDoc={previewHtml}
              className="w-full min-h-[420px] bg-white border-0 p-5"
              sandbox=""
            />
          </div>
        ) : null}
      </section>

      <section className={sectionClass}>
        <label htmlFor="test-email-input" className="block text-lg font-semibold text-charcoal mb-1">
          Test send
        </label>
        <p className="text-xs text-slate-gray mb-3">
          Sends the template for <strong>Email {activeStep}</strong> only (not the full 3-email sequence).
          Preview uses the test email above for <code className="text-[11px]">{'{{greeting}}'}</code> when valid;
          otherwise shows <strong>{SPONSOR_INVITE_PREVIEW_GREETING}</strong>.
          {'{{sponsor_link}}'} resolves to a preview invite URL.
        </p>
        <div className="flex flex-col sm:flex-row sm:items-start gap-3 max-w-xl">
          <div className="flex-1 min-w-0">
            <input
              id="test-email-input"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="test@example.com"
              value={testEmail}
              onChange={(e) => {
                setTestEmail(e.target.value);
                if (testEmailError) setTestEmailError(null);
              }}
              aria-invalid={testEmailError ? true : undefined}
              aria-describedby={testEmailError ? 'test-email-error' : undefined}
              className={`w-full px-3 py-2 rounded-lg border text-sm ${
                testEmailError ? 'border-red-500' : 'border-charcoal/15'
              }`}
            />
            {testEmailError ? (
              <p id="test-email-error" className="mt-1 text-xs text-red-600" role="alert">
                {testEmailError}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => void handleTestSend()}
            disabled={sendingTest}
            className="px-4 py-2 h-[42px] rounded-lg bg-electric-blue text-white text-sm font-medium disabled:opacity-50 shrink-0 sm:mt-0"
          >
            {sendingTest ? 'Sending…' : 'Send test'}
          </button>
        </div>
      </section>

      <section className={sectionClass}>
        <h2 className="text-lg font-semibold text-charcoal mb-3">Start campaign</h2>
        <div className="max-w-3xl">
          <label className="block text-sm font-medium text-charcoal mb-1">Recipient emails</label>
          <p className="text-xs text-slate-gray mb-3">
            Add a name (optional) and email per recipient, or use Bulk add.
          </p>

          <div className="flex flex-col gap-2">
            {recipientRows.map((row, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="text"
                  value={row.name}
                  onChange={(e) => updateRecipientRow(i, { name: e.target.value })}
                  placeholder="Recipient name"
                  aria-label={`Recipient ${i + 1} name`}
                  className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-charcoal/15 text-sm"
                />
                <input
                  type="email"
                  inputMode="email"
                  value={row.email}
                  onChange={(e) => updateRecipientRow(i, { email: e.target.value })}
                  placeholder="name@company.com"
                  aria-label={`Recipient ${i + 1} email`}
                  className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-charcoal/15 text-sm"
                />
                <button
                  type="button"
                  onClick={() => removeRecipientRow(i)}
                  aria-label={`Remove recipient ${i + 1}`}
                  className="shrink-0 p-2 text-slate-gray hover:text-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 mt-3">
            <button
              type="button"
              onClick={addRecipientRow}
              className="px-3 py-2 rounded-lg border border-charcoal/15 text-sm font-medium hover:bg-charcoal/5"
            >
              + Add recipient
            </button>
            <button
              type="button"
              onClick={() => setShowBulk((v) => !v)}
              className="px-3 py-2 rounded-lg border border-charcoal/15 text-sm font-medium hover:bg-charcoal/5"
            >
              Bulk add
            </button>
          </div>

          {campaignError ? (
            <p className="mt-2 text-xs text-red-600" role="alert">
              {campaignError}
            </p>
          ) : null}

          {showBulk ? (
            <div className="mt-4 rounded-xl border border-charcoal/15 bg-charcoal/5 p-4">
              <h3 className="text-sm font-semibold text-charcoal mb-1">Bulk add recipients</h3>
              <p className="text-xs text-slate-gray mb-1">
                One recipient per line. Use <strong>Name, email</strong> (comma, tab, or{' '}
                <code className="text-[11px]">&lt;email&gt;</code> format). Email-only lines are fine too.
              </p>
              <p className="text-xs text-slate-gray mb-3">
                Examples: <code className="text-[11px]">Jane Doe, jane@biotech.com</code>
                {'   '}
                <code className="text-[11px]">John Smith &lt;john@pharma.com&gt;</code>
                {'   '}
                <code className="text-[11px]">solo@company.com</code>
              </p>
              <textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                rows={5}
                placeholder={
                  'Jane Doe, jane@biotech.com\nJohn Smith <john@pharma.com>\nAcme Lead; lead@acme.com\nsolo@company.com'
                }
                className="w-full px-3 py-2 rounded-lg border border-charcoal/15 text-sm font-mono bg-white"
              />
              <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
                <label className="flex items-center gap-2 text-sm text-charcoal">
                  <input
                    type="checkbox"
                    checked={bulkReplace}
                    onChange={(e) => setBulkReplace(e.target.checked)}
                  />
                  Replace existing rows
                </label>
                <button
                  type="button"
                  onClick={applyBulkAdd}
                  className="px-5 py-2.5 rounded-xl bg-gradient-primary text-white text-sm font-semibold disabled:opacity-50"
                >
                  Add recipients
                </button>
              </div>
              {bulkFeedback ? <p className="mt-2 text-xs text-emerald-700">{bulkFeedback}</p> : null}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => void handleStartCampaign()}
          disabled={starting}
          className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-primary text-white font-semibold disabled:opacity-50"
        >
          {starting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          Start sponsor invite campaign
        </button>
      </section>

      {latestCampaignId ? (
        <section className={`${sectionClass} overflow-x-auto`}>
          <h2 className="text-lg font-semibold text-charcoal mb-3">Latest campaign recipients</h2>
          <p className="text-xs text-slate-gray mb-3">
            {recipients.filter((r) => ((r as { email_stage?: number | null }).email_stage ?? 0) >= 1).length}
            {' of '}
            {recipients.length} first emails sent · remaining recipients are queued and send
            automatically under the warmup cap (next batch within ~15 min).
          </p>
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="border-b border-charcoal/10 text-slate-gray">
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Invited email</th>
                <th className="py-2 pr-4">Stage</th>
                <th className="py-2 pr-4">Clicked</th>
                <th className="py-2 pr-4">Signup email</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {recipients.map((r) => (
                <tr key={r.id} className="border-b border-charcoal/5">
                  <td className="py-2 pr-4">
                    {(r as { invited_first_name?: string | null }).invited_first_name || '—'}
                  </td>
                  <td className="py-2 pr-4">{r.invited_email}</td>
                  <td className="py-2 pr-4">{r.email_stage ?? 0}</td>
                  <td className="py-2 pr-4">{r.link_clicked_at ? 'Yes' : '—'}</td>
                  <td className="py-2 pr-4">{r.signup_email || '—'}</td>
                  <td className="py-2">
                    {r.converted_user_id
                      ? 'Converted'
                      : r.unsubscribed_at
                        ? 'Unsubscribed'
                        : r.sequence_active
                          ? 'Active'
                          : r.sequence_stopped_reason || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}
    </div>
  );
};

export default SponsorInviteCampaignPanel;
